import { ApiEntity } from '@backstage/catalog-model';
import { CardTab, CodeSnippet, TabbedCard } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  apiDocsConfigRef,
  OpenApiDefinitionWidget,
  PlainApiDefinitionWidget,
} from '@backstage/plugin-api-docs';
import { useEntity } from '@backstage/plugin-catalog-react';
import Alert from '@mui/material/Alert';

import { useEntityPageModel } from './entity-page/useEntityPageModel';
import { UpsAuthProvider, useUpsAuthContext } from '../profile-linking/UpsAuthContext';
import { ProfileLinkingExplorer } from '../profile-linking/ProfileLinkingExplorer';

// Only this one API entity gets the shared-auth Swagger + embedded Profile
// Linking treatment below — every other API keeps the generic definitionWidget
// rendering untouched.
const UNIFIED_PROFILES_ENTITY_NAME = 'unified-profiles';

const UnifiedProfilesSwaggerSection = ({ definition }: { definition: string }) => {
  const { token } = useUpsAuthContext();

  // requestInterceptor is a real swagger-ui config option, passed through
  // untouched by OpenApiDefinitionWidget -> OpenApiDefinition -> SwaggerUI.
  const requestInterceptor = (req: { headers?: Record<string, string> }) => {
    if (token) {
      req.headers = { ...req.headers, Authorization: `Bearer ${token}` };
    }
    return req;
  };

  return (
    <>
      <OpenApiDefinitionWidget definition={definition} requestInterceptor={requestInterceptor} />
      <ProfileLinkingExplorer embedded />
    </>
  );
};

const formatJsonDefinition = (definition: string) => {
  try {
    return JSON.stringify(JSON.parse(definition), null, 2);
  } catch {
    return undefined;
  }
};

export const PrettyEntityApiDefinitionCard = () => {
  const { entity } = useEntity<ApiEntity>();
  const model = useEntityPageModel();
  const { getApiDefinitionWidget } = useApi(apiDocsConfigRef);

  if (!entity) {
    return <Alert severity="error">Could not fetch the API</Alert>;
  }

  const definition = model.apiDefinition.definition;
  if (!model.apiDefinition.available || !definition) {
    return <Alert severity="warning">No API definition available</Alert>;
  }

  const definitionWidget = getApiDefinitionWidget(entity);
  const entityTitle = model.title;
  const formattedJsonDefinition = formatJsonDefinition(definition);
  const rawLanguage = formattedJsonDefinition
    ? 'json'
    : definitionWidget?.rawLanguage ||
      model.apiDefinition.rawLanguage ||
      entity.spec.type;
  const rawLabel = formattedJsonDefinition ? 'JSON' : 'Raw';
  const rawDefinition = formattedJsonDefinition ?? definition;
  const rawContent = formattedJsonDefinition ? (
    <CodeSnippet
      language={rawLanguage}
      showCopyCodeButton
      text={rawDefinition}
    />
  ) : (
    <PlainApiDefinitionWidget
      definition={rawDefinition}
      language={rawLanguage}
    />
  );

  if (definitionWidget) {
    const isUnifiedProfiles = entity.metadata.name === UNIFIED_PROFILES_ENTITY_NAME;
    return (
      <TabbedCard title={entityTitle}>
        <CardTab label={definitionWidget.title} key="widget">
          {isUnifiedProfiles ? (
            <UpsAuthProvider>
              <UnifiedProfilesSwaggerSection definition={definition} />
            </UpsAuthProvider>
          ) : (
            definitionWidget.component(definition)
          )}
        </CardTab>
        <CardTab label={rawLabel} key="raw">
          {rawContent}
        </CardTab>
      </TabbedCard>
    );
  }

  return (
    <TabbedCard
      title={entityTitle}
      children={[
        <CardTab label={rawLabel} key="raw">
          {rawContent}
        </CardTab>,
      ]}
    />
  );
};
